import React from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useAuth } from '../../Helpers/Contexts/AuthContext';

/* Componentes puramente visuais do menu lateral (LeftDrawer). */

const expandToggleSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 0.4,
    borderRadius: 1,
    '&:hover': { bgcolor: 'action.hover' },
};

/**
 * Cabeçalho expansível de uma seção do menu.
 * - selected: fundo destacado | active: texto em destaque | bold: rótulo em negrito
 * - onSelect: clique no rótulo | onToggle: clique na seta (apenas expande/recolhe); sem onToggle a seta não aparece
 */
export function DrawerSection({ icon: Icon, label, open, onSelect, onToggle, selected, active, bold, first = false }) {
    return (
        <ListItem
            sx={{
                py: 0.7,
                px: 1.5,
                mb: 0.2,
                ...(first ? {} : { mt: 0.5 }),
                borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: selected ? 'action.selected' : 'transparent',
                color: active ? 'text.primary' : 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Box onClick={onSelect} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: 'inherit' }}>
                    <Icon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: bold ? 600 : 500,
                        noWrap: true,
                    }}
                />
            </Box>
            {onToggle && (
                <Box
                    component="span"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle();
                    }}
                    sx={expandToggleSx}
                >
                    {open ? (
                        <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    ) : (
                        <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    )}
                </Box>
            )}
        </ListItem>
    );
}

/** Item de sub-lista (indentado) dentro de uma seção expansível. */
export function DrawerSubItem({ label, selected, onClick, children }) {
    const { isDemo } = useAuth();
    return (
        <ListItem
            onClick={onClick}
            sx={{
                py: 0.7,
                px: 1.5,
                pl: 5.25,
                mb: 0.2,
                borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: selected ? 'action.selected' : 'transparent',
                color: selected ? 'text.primary' : 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                transition: 'all 0.15s ease',
            }}
        >
            <ListItemText
                primary={label}
                primaryTypographyProps={{
                    fontSize: 13.5,
                    fontWeight: selected ? 600 : 400,
                    noWrap: true,
                }}
            />
            {/* Modo demo: toda subaba leva o selo; fora dele, o que a tela pedir (ex.: loja demo para o dev) */}
            {isDemo ? <DemoChip /> : children}
        </ListItem>
    );
}

/** Selo "Demo" exibido ao lado de lojas de demonstração. */
export function DemoChip() {
    return (
        <Tooltip title="Modo Demonstração, sem dados reais" arrow placement="right">
            <Chip
                label="Demo"
                size="small"
                sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    ml: 0.5,
                    bgcolor: 'warning.main',
                    color: '#fff',
                    '& .MuiChip-label': { px: 0.8 },
                }}
            />
        </Tooltip>
    );
}

/** Spinner exibido enquanto as lojas são carregadas. */
export function DrawerLoading() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <CircularProgress size={18} color="primary" />
        </Box>
    );
}

/** Estado vazio — sem lojas e sem loading. */
export function DrawerEmptyStores() {
    return (
        <Typography
            variant="caption"
            sx={{ display: 'block', px: 5.25, py: 1, color: 'text.secondary', fontStyle: 'italic' }}
        >
            Nenhuma loja cadastrada
        </Typography>
    );
}
